import type { ParsedLessonPlanRow } from './lmExcelParser';

export interface ValidationError {
  row: number; // 0-based index into the rows array
  field: string;
  message: string;
}

/** Maps user-facing resource type labels to studyResourceTypeId */
export const RESOURCE_TYPE_MAP: Record<string, number> = {
  'download': 1,
  'reference': 5,
  'announcement': 6,
  'e-book': 13,
  'ebook': 13,
  'video': 31,
  'vimeo': 31,
  'youtube': 32,
  'youtube video': 32,
  'code editor': 37,
  'judge0': 37,
};

const DATE_REGEX = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Validate parsed lesson plan rows and return an array of errors.
 */
export function validateLessonPlanRows(rows: ParsedLessonPlanRow[]): ValidationError[] {
  const errors: ValidationError[] = [];

  rows.forEach((row, i) => {
    // Chapter required
    if (!row.chapter) {
      errors.push({ row: i, field: 'chapter', message: 'Chapter name is required' });
    }

    // Section required
    if (!row.section) {
      errors.push({ row: i, field: 'section', message: 'Section name is required' });
    }

    // Date format and validity validation
    // Check if raw date value exists but normalized date is null (invalid date)
    if (row._rawStartDate && !row.startDate) {
      const rawVal = row._rawStartDate instanceof Date
        ? row._rawStartDate.toISOString().split('T')[0]
        : String(row._rawStartDate);
      errors.push({
        row: i,
        field: 'startDate',
        message: `Invalid start date: "${rawVal}". Date does not exist in calendar.`
      });
    } else if (row.startDate && !DATE_REGEX.test(row.startDate)) {
      errors.push({ row: i, field: 'startDate', message: `Invalid start date format: "${row.startDate}". Expected YYYY-MM-DD.` });
    }

    if (row._rawEndDate && !row.endDate) {
      const rawVal = row._rawEndDate instanceof Date
        ? row._rawEndDate.toISOString().split('T')[0]
        : String(row._rawEndDate);
      errors.push({
        row: i,
        field: 'endDate',
        message: `Invalid end date: "${rawVal}". Date does not exist in calendar.`
      });
    } else if (row.endDate && !DATE_REGEX.test(row.endDate)) {
      errors.push({ row: i, field: 'endDate', message: `Invalid end date format: "${row.endDate}". Expected YYYY-MM-DD.` });
    }

    // End date >= start date
    if (row.startDate && row.endDate && DATE_REGEX.test(row.startDate) && DATE_REGEX.test(row.endDate)) {
      if (row.endDate < row.startDate) {
        errors.push({ row: i, field: 'endDate', message: 'End date must be on or after start date' });
      }
    }

    // Resource-specific validation
    if (row.resourceName) {
      // Resource type should be recognized
      if (row.resourceType) {
        const typeKey = row.resourceType.toLowerCase().trim();
        if (!RESOURCE_TYPE_MAP[typeKey]) {
          errors.push({
            row: i,
            field: 'resourceType',
            message: `Unknown resource type "${row.resourceType}". Valid types: Download, Reference, Announcement, E-Book, Video, YouTube, Code Editor`,
          });
        }
      } else {
        errors.push({ row: i, field: 'resourceType', message: 'Resource type is required when resource name is provided' });
      }

      // URL required for non-Announcement types
      const typeKey = (row.resourceType || '').toLowerCase().trim();
      const typeId = RESOURCE_TYPE_MAP[typeKey];
      if (typeId && typeId !== 6 && !row.resourceUrl) {
        errors.push({ row: i, field: 'resourceUrl', message: 'Resource URL is required for this resource type' });
      }
    }
  });

  return errors;
}

/**
 * Check if a specific row has any validation errors.
 */
export function rowHasErrors(errors: ValidationError[], rowIndex: number): boolean {
  return errors.some(e => e.row === rowIndex);
}

/**
 * Get all errors for a specific row.
 */
export function getRowErrors(errors: ValidationError[], rowIndex: number): ValidationError[] {
  return errors.filter(e => e.row === rowIndex);
}
